import React from 'react';

export function Card({ children, style, dark, onClick, ...props }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: dark ? 'var(--dark-card)' : 'var(--white)',
        borderRadius: 'var(--radius)',
        padding: 24,
        boxShadow: dark ? 'none' : 'var(--shadow)',
        color: dark ? 'white' : 'inherit',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.15s, box-shadow 0.15s',
        border: dark ? 'none' : '1px solid var(--gray-200)',
        ...style,
      }}
      {...props}
    >
      {children}
    </div>
  );
}

export function StatCard({ icon, label, value, subtitle, dark = true, style }) {
  return (
    <Card dark={dark} style={{ ...style }}>
      {icon && <div style={{ marginBottom: 12, fontSize: 24 }}>{icon}</div>}
      <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700 }}>{value}</div>
      {subtitle && <div style={{ fontSize: 13, opacity: 0.7, marginTop: 2 }}>{subtitle}</div>}
    </Card>
  );
}

export function PageHeader({ icon, title, subtitle, action }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start',
      justifyContent: 'space-between', marginBottom: 24,
      flexWrap: 'wrap', gap: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        {icon && (
          <div style={{
            width: 44, height: 44, borderRadius: 'var(--radius-sm)',
            background: 'var(--primary-light)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            color: 'var(--primary)',
          }}>
            {icon}
          </div>
        )}
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 2 }}>{title}</h1>
          {subtitle && <p style={{ color: 'var(--gray-600)', fontSize: 14 }}>{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
  );
}

export function TabBar({ tabs, activeTab, onTabChange }) {
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: 4,
      padding: 6, background: 'var(--gray-100)',
      borderRadius: 'var(--radius)', marginBottom: 24,
      justifyContent: 'center',
    }}>
      {tabs.map(tab => (
        <button
          key={tab.id}
          onClick={() => onTabChange(tab.id)}
          style={{
            padding: '10px 16px',
            borderRadius: 'var(--radius-sm)',
            fontSize: 13,
            fontWeight: activeTab === tab.id ? 600 : 400,
            background: activeTab === tab.id ? 'var(--primary)' : 'transparent',
            color: activeTab === tab.id ? 'white' : 'var(--gray-600)',
            display: 'flex', alignItems: 'center', gap: 6,
            whiteSpace: 'nowrap',
            transition: 'all 0.15s',
          }}
        >
          {tab.icon && (typeof tab.icon === 'string' ? <span>{tab.icon}</span> : <tab.icon size={14} />)}
          {tab.label}
        </button>
      ))}
    </div>
  );
}

export function Badge({ children, color = 'var(--primary)', style }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: 20,
      fontSize: 12,
      fontWeight: 600,
      color: 'white',
      background: color,
      ...style,
    }}>
      {children}
    </span>
  );
}

export function ProgressBar({ value, max = 100, color = 'var(--primary)', height = 8, style }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div style={{
      width: '100%', height, borderRadius: height,
      background: 'var(--gray-200)', overflow: 'hidden', ...style,
    }}>
      <div style={{
        width: `${Math.min(pct, 100)}%`, height: '100%',
        borderRadius: height, background: color,
        transition: 'width 0.4s ease',
      }} />
    </div>
  );
}

export function CircularProgress({ value, size = 80, strokeWidth = 8, color = 'var(--primary)' }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (value / 100) * circumference;
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size/2} cy={size/2} r={radius} fill="none"
        stroke="var(--gray-200)" strokeWidth={strokeWidth} />
      <circle cx={size/2} cy={size/2} r={radius} fill="none"
        stroke={color} strokeWidth={strokeWidth}
        strokeDasharray={circumference} strokeDashoffset={offset}
        strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.5s ease' }} />
      <text x="50%" y="50%" textAnchor="middle" dy=".3em"
        style={{ transform: 'rotate(90deg)', transformOrigin: 'center', fontSize: size * 0.22, fontWeight: 700, fill: 'currentColor' }}>
        {value}%
      </text>
    </svg>
  );
}

export function InputField({ label, value, onChange, onKeyDown, prefix, suffix, type = 'text', placeholder, style }) {
  return (
    <div style={{ marginBottom: 16, ...style }}>
      {label && <label style={{ display: 'block', fontSize: 14, fontWeight: 500, marginBottom: 6, color: 'var(--gray-700)' }}>{label}</label>}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        {prefix && (
          <span style={{
            position: 'absolute', left: 14, color: 'var(--gray-500)',
            fontSize: 14, fontWeight: 500, pointerEvents: 'none',
          }}>{prefix}</span>
        )}
        <input
          type={type}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          style={{
            width: '100%', padding: '10px 14px',
            paddingLeft: prefix ? 30 : 14,
            paddingRight: suffix ? 50 : 14,
            border: '1px solid var(--gray-300)',
            borderRadius: 'var(--radius-sm)',
            fontSize: 15, background: 'white',
          }}
        />
        {suffix && (
          <span style={{
            position: 'absolute', right: 14, color: 'var(--gray-500)',
            fontSize: 13, pointerEvents: 'none',
          }}>{suffix}</span>
        )}
      </div>
    </div>
  );
}
